import { forwardRef, useState, useImperativeHandle, useRef, useEffect } from 'react';
import { Search, CheckCircle2, AlertCircle, Loader2 } from 'lucide-react';
import { fetchCep, ViaCepResult, formatCEP } from '../utils/lookups';

interface Props {
  label?: string;
  value?: string;
  onChange?: (cep: string) => void;
  onAutofill: (data: ViaCepResult) => void;
  placeholder?: string;
  className?: string;
}

export interface CepLookupRef {
  lookup: () => Promise<void>;
}

const CepLookupField = forwardRef<CepLookupRef, Props>(function CepLookupField(
  { label = 'CEP', value = '', onChange, onAutofill, placeholder, className }, ref,
) {
  const [status, setStatus] = useState<'idle' | 'loading' | 'ok' | 'err'>('idle');
  const inputRef = useRef<HTMLInputElement | null>(null);
  const lastLookedRef = useRef<string>('');

  useEffect(() => {
    const c = (value || '').replace(/\D/g, '');
    if (c.length === 8 && c !== lastLookedRef.current) {
      lastLookedRef.current = c;
      doLookup(c);
    }
    if (c.length < 8) lastLookedRef.current = '';
  }, [value]);

  async function doLookup(cep: string) {
    const c = (cep || '').replace(/\D/g, '');
    if (c.length !== 8) return;
    setStatus('loading');
    const data = await fetchCep(c);
    if (data) {
      setStatus('ok');
      onAutofill(data);
    } else {
      setStatus('err');
    }
    setTimeout(() => setStatus('idle'), 2500);
  }

  useImperativeHandle(ref, () => ({
    lookup: async () => {
      await doLookup(inputRef.current?.value || '');
    },
  }));

  return (
    <div className={className}>
      <label className="field-label">{label}</label>
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          value={value}
          onChange={(e) => onChange?.(e.target.value)}
          onBlur={(e) => doLookup(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              doLookup((e.target as HTMLInputElement).value);
            }
          }}
          placeholder={placeholder ?? '00000-000'}
          maxLength={10}
          className="field-input pr-10"
          autoComplete="postal-code"
        />
        <button
          type="button"
          onClick={() => doLookup(inputRef.current?.value || value || '')}
          disabled={status === 'loading'}
          className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 hover:bg-slate-100 disabled:opacity-50 dark:hover:bg-white/10"
          title="Buscar no ViaCEP"
        >
          {status === 'loading' && <Loader2 className="h-4 w-4 animate-spin text-emerald-700 dark:text-emerald-400" />}
          {status === 'ok' && <CheckCircle2 className="h-4 w-4 text-emerald-700 dark:text-emerald-400" />}
          {status === 'err' && <AlertCircle className="h-4 w-4 text-rose-700 dark:text-rose-400" />}
          {status === 'idle' && <Search className="h-4 w-4 text-neutral-500 hover:text-emerald-700 dark:text-neutral-400 dark:hover:text-emerald-400" />}
        </button>
      </div>
      <p className="mt-1 text-[11px] text-neutral-600 dark:text-neutral-400">
        Preenche endereço automaticamente ao sair do campo.
      </p>
    </div>
  );
});

export default CepLookupField;
export { formatCEP };
